import { Select, Tag, TreeSelect } from "antd";
import SelectSearchNotFoundContent from "components/widgets/select-search-notfound-content";
import {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  forwardRef
} from "react";

interface LBSSelectFieldProps {
  onChange?: (selectedIds: string[]) => void;
  onBlur?: () => void;
  onClear?: () => void;
  disabled?: boolean;
  autoFocus?: boolean;
  initialValues: string[];
  open?: boolean;
  LBSOptionsMaster: any[];
  LBSDataInTreeFormat: any[];
  selectedLBSOptions?: any[];
  projectId: string;
}

const LBSSelectField = forwardRef<any, LBSSelectFieldProps>((props, ref) => {
  const {
    onChange,
    onBlur,
    onClear,
    disabled,
    autoFocus = false,
    initialValues,
    open,
    LBSOptionsMaster,
    LBSDataInTreeFormat,
    selectedLBSOptions = [], // only used when the field is disabled for nonpermitted users
    projectId
  } = props;

  const [selectedLabelValuePairs, setSelectedLabelValuePairs] = useState<any[]>(
    []
  );
  const internalRef = useRef<any>(null);
  const treeSelectRef = (ref as any) || internalRef;

  const formattedSelectedLBSOptions = useMemo(() => {
    return selectedLBSOptions?.map((option: any) => {
      return {
        label: option?.location?.path,
        value: option?.location?.id
      };
    });
  }, [selectedLBSOptions]);

  useEffect(() => {
    // The lookup resolves a display path only. The value stays the id we were
    // given: a location missing from LBSOptionsMaster must still round-trip, or
    // the next save posts null into feature_locations.location_id.
    const tmpSelectedLabelValuePairs = initialValues.map(
      (initialValue: any) => {
        const matchedOption = LBSOptionsMaster?.find(
          (option: any) => option.value === initialValue
        );
        return {
          label: matchedOption?.label,
          value: initialValue
        };
      }
    );

    setSelectedLabelValuePairs(tmpSelectedLabelValuePairs);
  }, [LBSOptionsMaster, initialValues]);

  const handleChange = useCallback(
    (selections: any) => {
      const selectedIds = selections
        .map((selection: any) => {
          return selection.value;
        })
        .filter(Boolean);
      onChange?.(selectedIds);
    },
    [onChange]
  );

  const treeTagRender = useCallback(
    (params: any) => {
      const { value }: any = params;
      if (!value) return <div />;

      const matchedOption = LBSOptionsMaster?.find(
        (option: any) => option.value === value
      );

      // Falls back to the raw id so an unresolved location still shows as
      // linked rather than rendering a blank tag the user cannot see or remove.
      let tagLabel = matchedOption?.label?.slice(0) || value;

      if (tagLabel.length > 33) {
        tagLabel = `...${tagLabel.slice(tagLabel.length - 33)}`;
      }

      const onPreventMouseDown = (event: React.MouseEvent<HTMLSpanElement>) => {
        event.preventDefault();
        event.stopPropagation();
      };

      const handleTagClose = () => {
        setTimeout(() => {
          if (treeSelectRef.current) {
            treeSelectRef.current.focus();
          }
        }, 0);
        const updatedSelections = selectedLabelValuePairs.filter(
          (item: any) => item.value !== value
        );
        setSelectedLabelValuePairs(updatedSelections);
        handleChange(updatedSelections);
      };

      return (
        <Tag
          title={matchedOption?.label || value}
          onMouseDown={onPreventMouseDown}
          closable
          style={{ marginRight: 2 }}
          onClose={handleTagClose}
        >
          {tagLabel}
        </Tag>
      );
    },
    [
      LBSOptionsMaster,
      selectedLabelValuePairs,
      handleChange,
      setSelectedLabelValuePairs,
      treeSelectRef
    ]
  );

  const selectTagRender = (params: any) => {
    const { label, closable, onClose } = params;
    const onPreventMouseDown = (event: React.MouseEvent<HTMLSpanElement>) => {
      event.preventDefault();
      event.stopPropagation();
    };

    let tagLabel = label?.slice(0) || "";

    if (tagLabel.length > 33) {
      tagLabel = `...${tagLabel.slice(tagLabel.length - 33)}`;
    }
    return (
      <Tag
        title={label}
        onMouseDown={onPreventMouseDown}
        closable={closable}
        onClose={onClose}
        style={{ marginRight: 3 }}
      >
        {tagLabel}
      </Tag>
    );
  };

  if (disabled) {
    return (
      <Select
        options={formattedSelectedLBSOptions}
        value={initialValues}
        mode="multiple"
        disabled={disabled}
        placeholder="Select Location"
        tagRender={selectTagRender}
        data-testid="lbs-select-field-disabled"
      />
    );
  }

  return (
    <TreeSelect
      ref={treeSelectRef}
      disabled={disabled}
      showSearch={{ treeNodeFilterProp: "title" }}
      style={{ width: "100%" }}
      className="lbs-tree-select w-full"
      classNames={{ popup: { root: "lbs-tree-select" } }}
      styles={{ popup: { root: { maxHeight: 400, overflow: "auto" } } }}
      value={selectedLabelValuePairs}
      placeholder="Select Location"
      treeData={LBSDataInTreeFormat}
      multiple
      tagRender={treeTagRender}
      treeCheckable
      showCheckedStrategy="SHOW_ALL"
      treeCheckStrictly
      onChange={handleChange}
      onBlur={onBlur}
      onClear={onClear}
      // eslint-disable-next-line jsx-a11y/no-autofocus
      autoFocus={autoFocus}
      open={open}
      notFoundContent={
        <SelectSearchNotFoundContent
          notFoundMsg="No Locations added. To add Locations to this project, click "
          linkTitle="Manage Locations"
          linkPath={`/project/${projectId}/settings/locations`}
        />
      }
      data-testid="lbs-select-field"
    />
  );
});

LBSSelectField.displayName = "LBSSelectField";

export default LBSSelectField;
